/**
 * Cart Drawer
 * ------------------------------------------------------------------
 * Vanilla JS, no dependency on the theme's bundled jQuery/empire.js.
 *
 * Integrates with Empire's existing `cartcount:update` window event,
 * which the theme already dispatches from every built-in add-to-cart
 * surface (product page, quick buy, quickshop). This drawer listens
 * for that event to refresh itself and open automatically whenever
 * the item count goes up.
 *
 * Content is (re)rendered using Shopify's Section Rendering API
 * against the `cart-drawer` section, so line items always come
 * straight from cart-item.liquid / cart-item-list.liquid — no markup
 * is duplicated in JS.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-cart-drawer]');
  if (!root) return;

  var previousItemCount = parseInt(root.getAttribute('data-item-count'), 10);
  if (isNaN(previousItemCount)) previousItemCount = 0;

  function routes() {
    return (window.Theme && window.Theme.routes) || {};
  }

  function cartUrl() {
    return routes().cart_url || '/cart';
  }

  function cartChangeUrl() {
    return (routes().cart_change_url || '/cart/change') + '.js';
  }

  function isCartPage() {
    return document.body.classList.contains('template-cart');
  }

  /**
   * Re-fetches the cart-drawer section HTML and swaps it in, so the
   * drawer always reflects the true state of the cart.
   */
  function refresh() {
    return fetch(cartUrl() + '?section_id=cart-drawer', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer]');
if (fresh) {
          root.innerHTML = fresh.innerHTML;
          root.setAttribute('data-item-count', fresh.getAttribute('data-item-count'));
          syncMinusButtons();
        }
      })
      .catch(function (error) {
        console.error('Cart Drawer: failed to refresh cart contents.', error);
      });
  }

  function open() {
    root.setAttribute('data-animation-state', 'open');
    root.setAttribute('data-animation', 'closed=>open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-open');
  }

  function close() {
    root.setAttribute('data-animation-state', 'hidden');
    root.setAttribute('data-animation', 'open=>hidden');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-open');
  }

function isOpen() {
    return root.getAttribute('data-animation-state') === 'open';
  }

  /**
   * Opening right after refresh() replaces the panel's DOM (or on
   * first load) needs a real paint of the closed/off-screen state
   * before flipping to open, otherwise the browser coalesces both
   * style changes into a single frame and the slide-in transition
   * never plays. Double rAF guarantees that paint happens first.
   */
  function openAfterPaint() {
    requestAnimationFrame(function () {
      requestAnimationFrame(open);
    });
  }

  /**
   * Sends a quantity change for a single line item (identified by its
   * cart line key, not index, so it stays correct even if the cart
   * has been modified elsewhere in the meantime), then notifies the
   * rest of the theme via the existing cartcount:update event.
   */
  function changeItem(key, quantity) {
    if (!key) return;

    fetch(cartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
      })
      .catch(function (error) {
        console.error('Cart Drawer: failed to update item.', error);
        // Content may be stale after a failed request; re-sync.
        refresh();
      });
  }

/**
   * The theme's own quantity-selector controller (which normally
   * enables/disables the "-" button based on the current quantity)
   * only runs inside the /cart page section, so it never touches
   * items rendered inside the drawer. This replicates that same
   * logic here.
   */
  function syncMinusButtons() {
    root.querySelectorAll('[data-quantity-selector]').forEach(function (selector) {
      var input = selector.querySelector('[data-quantity-input]');
      var minusWrapper = selector.querySelector('[data-button-wrapper-minus]');
      if (!input || !minusWrapper) return;

      var incrementValue = parseInt(input.getAttribute('data-increment-value'), 10) || 1;
      var quantity = parseInt(input.value, 10) || 0;

      if (quantity <= incrementValue) {
        minusWrapper.classList.add('quantity-selector__button-wrapper--disabled');
      } else {
        minusWrapper.classList.remove('quantity-selector__button-wrapper--disabled');
      }
    });
  }

  function stepQuantity(button, delta) {
    var row = button.closest('[data-cartitem-key]');
    var input = row && row.querySelector('[data-quantity-input]');
    if (!row || !input) return;

    var quantity = (parseInt(input.value, 10) || 0) + delta;
    if (quantity < 0) quantity = 0;
    input.value = quantity;
    syncMinusButtons();

    changeItem(row.getAttribute('data-cartitem-key'), quantity);
  }

  // Public API, in case other custom code on the store wants to
  // trigger the drawer (e.g. a "quick add" button elsewhere).
window.CartDrawer = {
    open: function () {
      refresh().then(openAfterPaint);
    },
    close: close,
    refresh: refresh,
    isOpen: isOpen,
  };

  // ------------------------------------------------------------------
  // Event delegation — works even after refresh() replaces innerHTML,
  // since listeners live on `document`, not on the swapped-out nodes.
  // ------------------------------------------------------------------

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-drawer-toggle]')) {
      event.preventDefault();
      window.CartDrawer.open();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      close();
      return;
    }

    if (event.target.closest('[data-cart-drawer-overlay]')) {
      close();
      return;
    }

    var removeLink = event.target.closest('[data-cartitem-remove]');
    if (removeLink && removeLink.closest('[data-cart-drawer]')) {
      event.preventDefault();
      var removeRow = removeLink.closest('[data-cartitem-key]');
      if (removeRow) changeItem(removeRow.getAttribute('data-cartitem-key'), 0);
      return;
    }

    var minusButton = event.target.closest('[data-quantity-minus]');
    if (minusButton && minusButton.closest('[data-cart-drawer]')) {
      event.preventDefault();
      stepQuantity(minusButton, -1);
      return;
    }

    var plusButton = event.target.closest('[data-quantity-plus]');
    if (plusButton && plusButton.closest('[data-cart-drawer]')) {
      event.preventDefault();
      stepQuantity(plusButton, 1);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-quantity-input]');
    if (!input || !input.closest('[data-cart-drawer]')) return;

    var row = input.closest('[data-cartitem-key]');
    if (!row) return;

    var quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < 0) quantity = 0;

    changeItem(row.getAttribute('data-cartitem-key'), quantity);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) close();
  });

  // ------------------------------------------------------------------
  // Listen for cart changes from the rest of the theme (product page
  // add-to-cart, quick buy, quickshop all already dispatch this).
  // ------------------------------------------------------------------
  window.addEventListener('cartcount:update', function (event) {
    var cart = event.detail;
    var newCount = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
    var didIncrease = newCount !== null && newCount > previousItemCount;

    if (newCount !== null) previousItemCount = newCount;

refresh().then(function () {
      if (didIncrease && !isCartPage()) openAfterPaint();
    });
  });

  // Fix up any items already rendered server-side on page load.
  syncMinusButtons();
})();